/**
 * ClusterHeatManager - Handles Mapbox GL clustering and heatmap layers
 * Optimized with separate lightweight datasets for better performance
 */
class ClusterHeatManager {
    static CONFIG = {
        // Zoom thresholds
        CLUSTER_MAX_ZOOM: 11,
        HEATMAP_MAX_ZOOM: 11.25,
        MARKER_MIN_ZOOM: 11, // When to show DOM markers instead
        // Cluster configuration
        CLUSTER: {
            RADIUS: 50,
            MIN_POINTS: 3,
            CIRCLE_COLOR: '#C7EEC8',
            CIRCLE_BLUR: 0.5,
            CIRCLE_OPACITY: 0.1,
            CIRCLE_RADIUS: 40
        },
        // Cluster count styling
        CLUSTER_COUNT: {
            TEXT_FONT: ['TWK Everett Book', 'Arial Unicode MS Regular'],
            TEXT_COLOR: '#ffffff',
            HALO_COLOR: '#505050',
            HALO_WIDTH: 3,
            HALO_BLUR: 1,
            SIZE_STEPS: [
                { threshold: 20, size: 16 },
                { threshold: 40, size: 18 },
                { threshold: 60, size: 20 },
                { threshold: 80, size: 24 },
            ]
        },
        // Heatmap styling
        HEATMAP: {
            COLOR_STOPS: [
                [0, 'rgba(0, 0, 255, 0)'],
                [0.2, 'rgba(224, 90, 37, 0.40)'],
                [0.4, 'rgba(224, 90, 37, 0.55)'],
                [0.6, 'rgba(224, 90, 37, 0.60)'],
                [0.8, 'rgba(224, 90, 37, 0.8)'],
                [1, 'rgba(252, 212, 132, 1)']
            ],
            RADIUS: {
                MIN_ZOOM: 0,
                MIN_RADIUS: 25,
                MAX_ZOOM: 8,
                MAX_RADIUS: 40
            },
            WEIGHT: 1,
            INTENSITY: {
                MIN_ZOOM: 0,
                MIN_INTENSITY: 1,
                MAX_ZOOM: 12,
                MAX_INTENSITY: 2.5
            },
            OPACITY: {
                STOPS: [
                    [0, 1],
                    [5, 1],
                    [10.5, 0.9],
                    [12, 0]
                ]
            }
        },
        // Fly animation for cluster clicks
        FLY_ANIMATION: {
            SPEED: 0.125,
            CURVE: 2,
            DURATION: 3000,
            TARGET_ZOOM_LOW: 9,    // When zoom < 8
            TARGET_ZOOM_HIGH: 12.5, // When zoom >= 8
            ZOOM_THRESHOLD: 8,
            BEARING: 0,
            PITCH: 0
        }
    };
    /**
     * Create ClusterHeatManager instance
     * @param {Object} mapManager - MapManager instance
     * @param {Object} dataManager - DataManager instance
     */
    constructor(mapManager, dataManager) {
        if (!mapManager || !dataManager) {
            throw new Error('ClusterHeatManager requires both mapManager and dataManager');
        }
        this.mapManager = mapManager;
        this.dataManager = dataManager;
        // State
        this.clustersInitialized = false;
        this.heatmapInitialized = false;
        // Source IDs
        this.CLUSTER_SOURCE_ID = 'locations-for-clusters';
        this.HEATMAP_SOURCE_ID = 'locations-for-heatmap';
        // Layer IDs
        this.CLUSTER_LAYER_ID = 'locations-cluster';
        this.CLUSTER_COUNT_LAYER_ID = 'locations-cluster-count';
        this.HEATMAP_LAYER_ID = 'locations-heatmap';
    }
    // ! INITIALIZE CLUSTER + HEATMAP
    /**
     * Initialize both clustering and heatmap layers
     * Call this after map is loaded
     */
    initialize() {
        this.addClusterLayers();
        this.addHeatmapLayer();
    }
    // ! ADD CLUSTER LAYER
    /**
     * Add clustering layers to the map
     * Includes circle layer and count text layer
     */
    addClusterLayers() {
        const map = this.mapManager.getMap();
        if (!map) {
            return;
        }
        // Check if source already exists
        if (map.getSource(this.CLUSTER_SOURCE_ID)) {
            this._updateClusterSource();
            return;
        }
        // Create lightweight GeoJSON for clustering (coordinates only)
        const clusterGeoJSON = this._createLightweightGeoJSON();
        // Add cluster data source
        map.addSource(this.CLUSTER_SOURCE_ID, {
            type: 'geojson',
            data: clusterGeoJSON,
            cluster: true,
            clusterMaxZoom: ClusterHeatManager.CONFIG.CLUSTER_MAX_ZOOM,
            clusterRadius: ClusterHeatManager.CONFIG.CLUSTER.RADIUS,
            clusterMinPoints: ClusterHeatManager.CONFIG.CLUSTER.MIN_POINTS
        });
        // Add cluster circle layer
        this._addClusterCircleLayer(map);
        // Add cluster count text layer
        this._addClusterCountLayer(map);
        // Add click interaction
        this._setupClusterClickHandler(map);
        this.clustersInitialized = true;
    }
    /**
     * Add cluster circle layer (visual circles)
     * @private
     */
    _addClusterCircleLayer(map) {
        const config = ClusterHeatManager.CONFIG.CLUSTER;
        map.addLayer({
            id: this.CLUSTER_LAYER_ID,
            type: 'circle',
            source: this.CLUSTER_SOURCE_ID,
            maxzoom: ClusterHeatManager.CONFIG.CLUSTER_MAX_ZOOM,
            paint: {
                'circle-color': config.CIRCLE_COLOR,
                'circle-blur': config.CIRCLE_BLUR,
                'circle-opacity': config.CIRCLE_OPACITY,
                'circle-radius': config.CIRCLE_RADIUS
            }
        });
    }
    /**
     * Add cluster count text layer (numbers)
     * @private
     */
    _addClusterCountLayer(map) {
        const config = ClusterHeatManager.CONFIG.CLUSTER_COUNT;
        // Build text size steps for Mapbox expression
        const textSizeExpression = ['step', ['get', 'point_count']];
        textSizeExpression.push(18); // Base size
        config.SIZE_STEPS.forEach(step => {
            textSizeExpression.push(step.threshold, step.size);
        });
        map.addLayer({
            id: this.CLUSTER_COUNT_LAYER_ID,
            type: 'symbol',
            source: this.CLUSTER_SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': config.TEXT_FONT,
                'text-size': textSizeExpression,
                'text-allow-overlap': false,
                'text-ignore-placement': true
            },
            paint: {
                'text-color': config.TEXT_COLOR,
                'text-halo-color': config.HALO_COLOR,
                'text-halo-width': config.HALO_WIDTH,
                'text-halo-blur': config.HALO_BLUR
            }
        });
    }
    // ! CLUSTER EVENT HANDLER
    /**
     * Set up click handler for clusters
     * @private
     */
    _setupClusterClickHandler(map) {
        map.on('click', this.CLUSTER_LAYER_ID, (e) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: [this.CLUSTER_LAYER_ID]
            });
            if (features.length === 0) return;
            const clusterId = features[0].properties.cluster_id;
            const currentZoom = map.getZoom();
            const config = ClusterHeatManager.CONFIG.FLY_ANIMATION;
            // Determine target zoom based on current zoom
            const targetZoom = currentZoom < config.ZOOM_THRESHOLD
                ? config.TARGET_ZOOM_LOW
                : config.TARGET_ZOOM_HIGH;
            // Get cluster expansion zoom and fly to it
            map.getSource(this.CLUSTER_SOURCE_ID).getClusterExpansionZoom(
                clusterId,
                (err, zoom) => {
                    if (err) {
                        return;
                    }
                    // Fly to cluster location
                    map.flyTo({
                        center: features[0].geometry.coordinates,
                        zoom: targetZoom,
                        speed: config.SPEED,
                        curve: config.CURVE,
                        duration: config.DURATION,
                        essential: true,
                        bearing: config.BEARING,
                        pitch: config.PITCH,
                        easing: (t) => 1 - Math.pow(1 - t, 5)
                    });
                }
            );
        });
        // Change cursor on hover
        map.on('mouseenter', this.CLUSTER_LAYER_ID, () => {
            map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', this.CLUSTER_LAYER_ID, () => {
            map.getCanvas().style.cursor = '';
        });
    }
    // ! ADD HEATMAP LAYER
    /**
     * Add heatmap layer to the map
     */
    addHeatmapLayer() {
        const map = this.mapManager.getMap();
        if (!map) {
            return;
        }
        // Check if source already exists
        if (map.getSource(this.HEATMAP_SOURCE_ID)) {
            this._updateHeatmapSource();
            return;
        }
        // Create lightweight GeoJSON for heatmap
        const heatmapGeoJSON = this._createLightweightGeoJSON();
        // Add heatmap data source
        map.addSource(this.HEATMAP_SOURCE_ID, {
            type: 'geojson',
            data: heatmapGeoJSON
        });
        // Add heatmap layer with styling
        this._addHeatmapStyleLayer(map);
        this.heatmapInitialized = true;
    }
    /**
     * Add heatmap style layer
     * @private
     */
    _addHeatmapStyleLayer(map) {
        const config = ClusterHeatManager.CONFIG.HEATMAP;
        // Build color stops for Mapbox expression
        const colorExpression = ['interpolate', ['linear'], ['heatmap-density']];
        config.COLOR_STOPS.forEach(([density, color]) => {
            colorExpression.push(density, color);
        });
        // Build opacity stops
        const opacityExpression = ['interpolate', ['linear'], ['zoom']];
        config.OPACITY.STOPS.forEach(([zoom, opacity]) => {
            opacityExpression.push(zoom, opacity);
        });
        map.addLayer({
            id: this.HEATMAP_LAYER_ID,
            type: 'heatmap',
            source: this.HEATMAP_SOURCE_ID,
            maxzoom: ClusterHeatManager.CONFIG.HEATMAP_MAX_ZOOM,
            paint: {
                'heatmap-color': colorExpression,
                'heatmap-radius': [
                    'interpolate',
                    ['exponential', 1.5],
                    ['zoom'],
                    config.RADIUS.MIN_ZOOM, config.RADIUS.MIN_RADIUS,
                    config.RADIUS.MAX_ZOOM, config.RADIUS.MAX_RADIUS
                ],
                'heatmap-weight': config.WEIGHT,
                'heatmap-intensity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    config.INTENSITY.MIN_ZOOM, config.INTENSITY.MIN_INTENSITY,
                    config.INTENSITY.MAX_ZOOM, config.INTENSITY.MAX_INTENSITY
                ],
                'heatmap-opacity': opacityExpression
            },
            slot: 'top'
        });
    }
    /**
     * Create lightweight GeoJSON (coordinates only, no heavy properties)
     * OPTIMIZATION: Separate dataset for clustering vs marker rendering
     * @private
     */
    _createLightweightGeoJSON() {
        return {
            type: 'FeatureCollection',
            features: this.dataManager.allLocations.map(feature => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: feature.geometry.coordinates
                },
                properties: {
                    // Only essential properties for clustering
                    id: feature.properties.id
                    // NO images, NO names, NO styling data = faster!
                }
            }))
        };
    }
    // ! UPDATE FOR FILTERING
    /**
     * Update cluster source with new data (after filtering)
     */
    updateClusters(filteredLocations) {
        const map = this.mapManager.getMap();
        if (!map || !map.getSource(this.CLUSTER_SOURCE_ID)) {
            return;
        }
        const clusterGeoJSON = {
            type: 'FeatureCollection',
            features: filteredLocations.map(feature => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: feature.geometry.coordinates
                },
                properties: {
                    id: feature.properties.id
                }
            }))
        };
        map.getSource(this.CLUSTER_SOURCE_ID).setData(clusterGeoJSON);
    }
    /**
     * Update heatmap source with new data (after filtering)
     */
    updateHeatmap(filteredLocations) {
        const map = this.mapManager.getMap();
        if (!map || !map.getSource(this.HEATMAP_SOURCE_ID)) {
            return;
        }
        const heatmapGeoJSON = {
            type: 'FeatureCollection',
            features: filteredLocations.map(feature => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: feature.geometry.coordinates
                },
                properties: {
                    id: feature.properties.id
                }
            }))
        };
        map.getSource(this.HEATMAP_SOURCE_ID).setData(heatmapGeoJSON);
    }
    /**
     * Update both clusters and heatmap together
     * @param {Array} filteredLocations - Filtered GeoJSON features
     */
    update(filteredLocations) {
        this.updateClusters(filteredLocations);
        this.updateHeatmap(filteredLocations);
    }
    /**
     * Update cluster source (private helper)
     * @private
     */
    _updateClusterSource() {
        const clusterGeoJSON = this._createLightweightGeoJSON();
        const map = this.mapManager.getMap();
        map.getSource(this.CLUSTER_SOURCE_ID).setData(clusterGeoJSON);
    }
    /**
     * Update heatmap source (private helper)
     * @private
     */
    _updateHeatmapSource() {
        const heatmapGeoJSON = this._createLightweightGeoJSON();
        const map = this.mapManager.getMap();
        map.getSource(this.HEATMAP_SOURCE_ID).setData(heatmapGeoJSON);
    }
    // ! UPDATE VISIBILITY ON ZOOM
    /**
     * Show or hide cluster layers based on zoom
     * @param {number} zoom - Current zoom level
     */
    toggleClustersVisibility(zoom) {
        const map = this.mapManager.getMap();
        if (!map) return;
        const visible = zoom < ClusterHeatManager.CONFIG.CLUSTER_MAX_ZOOM;
        const visibility = visible ? 'visible' : 'none';
        if (map.getLayer(this.CLUSTER_LAYER_ID)) {
            map.setLayoutProperty(this.CLUSTER_LAYER_ID, 'visibility', visibility);
        }
        if (map.getLayer(this.CLUSTER_COUNT_LAYER_ID)) {
            map.setLayoutProperty(this.CLUSTER_COUNT_LAYER_ID, 'visibility', visibility);
        }
    }
    /**
     * Show or hide heatmap layer based on zoom
     * @param {number} zoom - Current zoom level
     */
    toggleHeatmapVisibility(zoom) {
        const map = this.mapManager.getMap();
        if (!map || !map.getLayer(this.HEATMAP_LAYER_ID)) return;
        const visible = zoom < ClusterHeatManager.CONFIG.HEATMAP_MAX_ZOOM;
        const visibility = visible ? 'visible' : 'none';
        map.setLayoutProperty(this.HEATMAP_LAYER_ID, 'visibility', visibility);
    }
    /**
     * Remove cluster layers from map
     */
    removeClusters() {
        const map = this.mapManager.getMap();
        if (!map) return;
        if (map.getLayer(this.CLUSTER_COUNT_LAYER_ID)) {
            map.removeLayer(this.CLUSTER_COUNT_LAYER_ID);
        }
        if (map.getLayer(this.CLUSTER_LAYER_ID)) {
            map.removeLayer(this.CLUSTER_LAYER_ID);
        }
        if (map.getSource(this.CLUSTER_SOURCE_ID)) {
            map.removeSource(this.CLUSTER_SOURCE_ID);
        }
        this.clustersInitialized = false;
    }
    /**
     * Remove heatmap layer from map
     */
    removeHeatmap() {
        const map = this.mapManager.getMap();
        if (!map) return;
        if (map.getLayer(this.HEATMAP_LAYER_ID)) {
            map.removeLayer(this.HEATMAP_LAYER_ID);
        }
        if (map.getSource(this.HEATMAP_SOURCE_ID)) {
            map.removeSource(this.HEATMAP_SOURCE_ID);
        }
        this.heatmapInitialized = false;
    }
    /**
     * Check if clusters are initialized
     * @returns {boolean}
     */
    areClustersInitialized() {
        return this.clustersInitialized;
    }
    /**
     * Check if heatmap is initialized
     * @returns {boolean}
     */
    isHeatmapInitialized() {
        return this.heatmapInitialized;
    }
    /**
     * Update configuration
     * @param {Object} config - New configuration to merge
     */
    updateConfig(config) {
        Object.assign(ClusterHeatManager.CONFIG, config);
    }
}
// ! EXPORT
export default ClusterHeatManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') {
    window.ClusterHeatManager = ClusterHeatManager;
}